import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router';

import Iconify from '../../../components/iconify';
import Scrollbar from '../../../components/scrollbar';
import { useLocales } from '../../../locales';
import { useReleaseNotes } from '../../../release-notes/ReleaseNotesContext';
import { subscribeToReleaseNotesOpen, useReleaseNotesState } from '../../../release-notes/storage';
import { ReleaseNoteChangeType, ReleaseNoteLanguage } from '../../../release-notes/types';

const changeColors: Record<ReleaseNoteChangeType, 'info' | 'success' | 'warning'> = {
  new: 'success',
  improved: 'info',
  fixed: 'warning',
};

const changeTypeOrder: ReleaseNoteChangeType[] = ['new', 'improved', 'fixed'];

export default function ReleaseNotesDrawer() {
  const { translate, currentLang } = useLocales();
  const { releaseNotes, latestReleaseNote, isLoading, hasError } = useReleaseNotes();
  const { lastSeenReleaseId, markReleaseAsSeen } = useReleaseNotesState();
  const [open, setOpen] = useState(false);
  const releaseLanguage: ReleaseNoteLanguage = currentLang.value === 'en' ? 'en' : 'de';

  const unreadCount = useMemo(() => {
    if (!latestReleaseNote) return 0;

    const lastSeenIndex = releaseNotes.findIndex((note) => note.id === lastSeenReleaseId);
    return lastSeenIndex < 0 ? releaseNotes.length : lastSeenIndex;
  }, [lastSeenReleaseId, latestReleaseNote, releaseNotes]);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  useEffect(() => subscribeToReleaseNotesOpen(handleOpen), [handleOpen]);

  useEffect(() => {
    if (open && latestReleaseNote) markReleaseAsSeen(latestReleaseNote.id);
  }, [latestReleaseNote, markReleaseAsSeen, open]);

  const formatPublishedAt = (publishedAt: string) =>
    new Intl.DateTimeFormat(currentLang.value, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    }).format(new Date(`${publishedAt}T00:00:00`));

  return (
    <>
      <Tooltip title={`${translate('releaseNotes.open')}`}>
        <IconButton
          onClick={handleOpen}
          aria-label={`${translate('releaseNotes.open')}`}
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.25,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Badge color="error" variant="dot" invisible={unreadCount === 0}>
            <Iconify width={23} icon="material-symbols:campaign-rounded" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 460 },
            maxWidth: '100%',
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 3, py: 2.5 }}>
          <Box
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 40,
              height: 40,
              borderRadius: 1.5,
              color: 'primary.main',
              bgcolor: 'primary.lighter',
            }}
          >
            <Iconify width={25} icon="material-symbols:campaign-rounded" />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">{`${translate('releaseNotes.title')}`}</Typography>
            <Typography variant="body2" color="text.secondary">
              {`${translate('releaseNotes.description')}`}
            </Typography>
          </Box>
          <Tooltip title={`${translate('releaseNotes.close')}`}>
            <IconButton
              onClick={() => setOpen(false)}
              aria-label={`${translate('releaseNotes.close')}`}
            >
              <Iconify icon="eva:close-fill" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Divider />

        <Scrollbar sx={{ flexGrow: 1 }}>
          <Stack spacing={3} sx={{ p: 3 }}>
            {isLoading && (
              <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary">
                  {`${translate('releaseNotes.loading')}`}
                </Typography>
              </Stack>
            )}

            {!isLoading && hasError && (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 8 }}>
                {`${translate('releaseNotes.unavailable')}`}
              </Typography>
            )}

            {!isLoading && !hasError && releaseNotes.length === 0 && (
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 8 }}>
                {`${translate('releaseNotes.empty')}`}
              </Typography>
            )}

            {!isLoading &&
              !hasError &&
              releaseNotes.map((release, index) => (
                <Box
                  key={release.id}
                  sx={{
                    position: 'relative',
                    pl: 2.5,
                    borderLeft: 2,
                    borderColor: index === 0 ? 'primary.main' : 'divider',
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatPublishedAt(release.publishedAt)}
                    </Typography>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`${translate('releaseNotes.version')} ${release.version}`}
                    />
                    {index === 0 && (
                      <Chip
                        size="small"
                        color="primary"
                        label={`${translate('releaseNotes.latest')}`}
                      />
                    )}
                  </Stack>

                  <Typography variant="h6" sx={{ mb: 0.75 }}>
                    {release.title[releaseLanguage]}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {release.summary[releaseLanguage]}
                  </Typography>

                  <Stack spacing={2}>
                    {changeTypeOrder.map((changeType) => {
                      const changes = release.changes.filter(
                        (change) => change.type === changeType
                      );

                      if (changes.length === 0) return null;

                      return (
                        <Stack key={changeType} spacing={0.75} alignItems="flex-start">
                          <Chip
                            size="small"
                            color={changeColors[changeType]}
                            label={`${translate(`releaseNotes.changeTypes.${changeType}`)}`}
                            sx={{ minWidth: 82 }}
                          />

                          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                            {changes.map((change, changeIndex) => (
                              <Box
                                component="li"
                                key={`${release.id}-${changeType}-${changeIndex}`}
                                sx={{ mb: 0.5, '&:last-child': { mb: 0 } }}
                              >
                                {change.href ? (
                                  <Link component={RouterLink} to={change.href} variant="body2">
                                    {change.text[releaseLanguage]}
                                  </Link>
                                ) : (
                                  <Typography variant="body2">
                                    {change.text[releaseLanguage]}
                                  </Typography>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
          </Stack>
        </Scrollbar>
      </Drawer>
    </>
  );
}
